import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

import { leaveStatusChipColor, leaveTypeLabel } from "../leave-requests/leave-request-helpers";
import {
  staffCalendarStatusSymbol,
  staffCalendarStatusSx,
} from "../shared/staff-calendar-status-tokens";

/** Statuses that appear on leave / attendance calendars (cancelled & declined stay off). */
export const CALENDAR_LEAVE_STATUSES = new Set(["PENDING", "APPROVED"]);

export function isCalendarLeaveStatus(status: string | null | undefined): boolean {
  return status != null && CALENDAR_LEAVE_STATUSES.has(status);
}

export type OfficeLeaveCalendarEntry = {
  id: string;
  employeeId: number;
  employeeName: string;
  leaveType: string;
  status: string;
  startDate: string;
  endDate: string;
};

export type OfficeLeaveCalendarEmployee = {
  id: number;
  name: string;
  department: string | null;
};

export type OfficeLeaveCalendarResponse = {
  month: string;
  type?: string;
  viewerEmployeeId?: number | null;
  employees?: OfficeLeaveCalendarEmployee[];
  days: Record<string, OfficeLeaveCalendarEntry[]>;
};

export type LeaveEmployeeRow = {
  employeeId: number;
  name: string;
  department: string | null;
  /** Leave entries for each day-of-month that has leave (usually 0–1). */
  days: Record<number, OfficeLeaveCalendarEntry[]>;
  summary: {
    leaveDays: number;
    approvedDays: number;
    pendingDays: number;
  };
};

export type LeaveDayDrilldown = {
  employee: LeaveEmployeeRow;
  day: number;
  entries: OfficeLeaveCalendarEntry[];
};

function emptySummary() {
  return { leaveDays: 0, approvedDays: 0, pendingDays: 0 };
}

function summarizeDays(days: Record<number, OfficeLeaveCalendarEntry[]>) {
  let leaveDays = 0;
  let approvedDays = 0;
  let pendingDays = 0;
  for (const entries of Object.values(days)) {
    if (entries.length === 0) continue;
    leaveDays += 1;
    if (entries.some((e) => e.status === "PENDING")) pendingDays += 1;
    else if (entries.some((e) => e.status === "APPROVED")) approvedDays += 1;
  }
  return { leaveDays, approvedDays, pendingDays };
}

/**
 * Build one row per employee from the office-calendar payload.
 * When `employees` is provided (API roster), every staff is included even with zero leave.
 * Falls back to leave-only rows when the roster is missing (legacy).
 */
export function buildLeaveEmployeeRows(
  days: Record<string, OfficeLeaveCalendarEntry[]> | null | undefined,
  employees?: OfficeLeaveCalendarEmployee[] | null
): LeaveEmployeeRow[] {
  const byEmployee = new Map<number, LeaveEmployeeRow>();

  if (Array.isArray(employees)) {
    for (const emp of employees) {
      if (!emp || !Number.isFinite(emp.id)) continue;
      byEmployee.set(emp.id, {
        employeeId: emp.id,
        name: emp.name || `Employee ${emp.id}`,
        department: emp.department ?? null,
        days: {},
        summary: emptySummary(),
      });
    }
  }

  if (days && typeof days === "object") {
    for (const [dayKey, entries] of Object.entries(days)) {
      const day = Number(dayKey);
      if (!Number.isFinite(day) || day < 1 || !Array.isArray(entries)) continue;

      for (const entry of entries) {
        if (!entry || !Number.isFinite(entry.employeeId)) continue;
        // Cancelled / declined (or any non-calendar status) must not appear on the grid.
        if (!isCalendarLeaveStatus(entry.status)) continue;
        let row = byEmployee.get(entry.employeeId);
        if (!row) {
          row = {
            employeeId: entry.employeeId,
            name: entry.employeeName || `Employee ${entry.employeeId}`,
            department: null,
            days: {},
            summary: emptySummary(),
          };
          byEmployee.set(entry.employeeId, row);
        }
        if (!row.days[day]) row.days[day] = [];
        if (!row.days[day].some((e) => e.id === entry.id)) {
          row.days[day].push(entry);
        }
      }
    }
  }

  for (const row of byEmployee.values()) {
    row.summary = summarizeDays(row.days);
  }

  return [...byEmployee.values()].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
  );
}

/** Mirrors API `isOfficeCalendarLeaveVisibleToPortalViewer` — defense in depth on the client. */
export function isLeaveCalendarEntryVisibleToPortalViewer(
  entry: OfficeLeaveCalendarEntry,
  linkedEmployeeId: number | null
): boolean {
  if (entry.status !== "PENDING") return true;
  return linkedEmployeeId != null && entry.employeeId === linkedEmployeeId;
}

export function filterLeaveCalendarDaysForViewer(
  days: Record<string, OfficeLeaveCalendarEntry[]>,
  opts: { canOfficeView: boolean; linkedEmployeeId: number | null }
): Record<string, OfficeLeaveCalendarEntry[]> {
  if (opts.canOfficeView) return days;
  const filtered: Record<string, OfficeLeaveCalendarEntry[]> = {};
  for (const [dayKey, entries] of Object.entries(days)) {
    if (!Array.isArray(entries)) continue;
    const visible = entries.filter((entry) =>
      isLeaveCalendarEntryVisibleToPortalViewer(entry, opts.linkedEmployeeId)
    );
    if (visible.length > 0) filtered[dayKey] = visible;
  }
  return filtered;
}

/**
 * Portal-only: approved leave for all staff; pending only on the linked employee row.
 * Rebuilds rows so summary columns (L / A / P) do not expose colleagues' pending counts.
 */
export function filterLeaveCalendarForViewer(
  rows: LeaveEmployeeRow[],
  days: Record<string, OfficeLeaveCalendarEntry[]>,
  opts: { canOfficeView: boolean; linkedEmployeeId: number | null }
): {
  rows: LeaveEmployeeRow[];
  days: Record<string, OfficeLeaveCalendarEntry[]>;
} {
  if (opts.canOfficeView) {
    return { rows, days };
  }
  const filteredDays = filterLeaveCalendarDaysForViewer(days, opts);
  const employees: OfficeLeaveCalendarEmployee[] = rows.map((row) => ({
    id: row.employeeId,
    name: row.name,
    department: row.department,
  }));
  return {
    days: filteredDays,
    rows: buildLeaveEmployeeRows(filteredDays, employees),
  };
}

/** Filter rows by name / id substring (case-insensitive). */
export function filterLeaveEmployeeRows(
  rows: LeaveEmployeeRow[],
  nameQuery: string
): LeaveEmployeeRow[] {
  const q = nameQuery.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => r.name.toLowerCase().includes(q) || String(r.employeeId).includes(q));
}

/** Tooltip lines for a grid cell (type + status; name optional for multi). */
export function leaveGridCellTooltip(
  entries: OfficeLeaveCalendarEntry[],
  opts?: { includeName?: boolean }
): string {
  if (entries.length === 0) return "No leave";
  return entries
    .map((e) => {
      const type = leaveTypeLabel(e.leaveType);
      const status = e.status ? e.status.charAt(0) + e.status.slice(1).toLowerCase() : "";
      const head = opts?.includeName ? `${e.employeeName} · ` : "";
      return `${head}${type}${status ? ` · ${status}` : ""}`;
    })
    .join(" · ");
}

export function leaveGridCellTone(
  entries: OfficeLeaveCalendarEntry[]
): "quiet" | "pending" | "approved" {
  if (entries.length === 0) return "quiet";
  if (entries.some((e) => e.status === "PENDING")) return "pending";
  if (entries.some((e) => e.status === "APPROVED")) return "approved";
  return "quiet";
}

/** Compact cell label: Lp pending, A approved (pending wins when both). */
export function leaveGridCellLabel(entries: OfficeLeaveCalendarEntry[]): string {
  if (entries.length === 0) return "";
  const tone = leaveGridCellTone(entries);
  if (tone === "pending") return staffCalendarStatusSymbol("pendingLeave", "leave");
  if (tone === "approved") return staffCalendarStatusSymbol("approvedLeave", "leave");
  return staffCalendarStatusSymbol("approvedLeave", "attendance");
}

export function leaveGridCellSx(
  theme: Theme,
  entries: OfficeLeaveCalendarEntry[],
  opts?: { isOffColumn?: boolean }
): Record<string, unknown> {
  const tone = leaveGridCellTone(entries);
  const base: Record<string, unknown> = {};
  if (opts?.isOffColumn) {
    base.bgcolor = alpha(theme.palette.action.disabledBackground, 0.35);
  }
  if (tone === "pending") {
    return { ...base, ...staffCalendarStatusSx(theme, "pendingLeave", { fontWeight: 700 }) };
  }
  if (tone === "approved") {
    return { ...base, ...staffCalendarStatusSx(theme, "approvedLeave", { fontWeight: 700 }) };
  }
  return base;
}

/** Whether the viewer may deep-link to a leave show page. */
export function canOpenLeaveShow(
  entry: OfficeLeaveCalendarEntry,
  opts: { canOfficeView: boolean; linkedEmployeeId: number | null }
): boolean {
  if (opts.canOfficeView) return true;
  return opts.linkedEmployeeId != null && entry.employeeId === opts.linkedEmployeeId;
}

export { leaveStatusChipColor, leaveTypeLabel };
